import { useEffect, useMemo, useState } from 'react';
import { TRACE_STAGES, type TraceStep } from '@shared/trace';
import { useStore } from '../state/store';
import { Button, STEP_STATUS_LABEL, formatDuration, formatTime } from '../design/primitives';
import { StageDetail } from './StageDetail';
import './theater.css';

/**
 * The Action Theater: one governed action, stage by stage, as it happens.
 *
 * This is the part of the demo that does the persuading. A verdict on its own is
 * an assertion; the same verdict with the reasoning laid out — what was asked,
 * what it resolved to, what the cached engine decided offline, what the control
 * plane answered, which rule matched — is an argument.
 *
 * Three decisions worth knowing:
 * - Stages that did not run are shown as skipped with a reason, never hidden.
 *   "This never ran, and here is why" is information; an absent row is not.
 * - The stage that decided the outcome expands itself. Everything else starts
 *   collapsed, because eleven open stages is a wall nobody reads.
 * - The plain-language verdict sits above the detail, not below it.
 */
export function ActionTheater() {
  const { focusedTrace, approvals, resolveApproval, bootstrap } = useStore();

  if (!focusedTrace) {
    return (
      <div className="th">
        <div className="th-empty">
          <p style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--c-text)' }}>Nothing running yet</p>
          <p style={{ marginTop: 'var(--s-3)', maxWidth: '34ch', marginInline: 'auto' }}>
            Run a guided scenario, or use any operation in the bank. Every action appears here with its full decision
            path — including the ones that get refused.
          </p>
        </div>
      </div>
    );
  }

  const pending = approvals.find(approval => approval.traceId === focusedTrace.id);
  const approvers = (bootstrap?.estate.staff ?? []).filter((person: any) => person.approver);

  return (
    <div className="th">
      <header className="th-head">
        <h2>{focusedTrace.title}</h2>
        <div className="th-sub">
          <span>{focusedTrace.operatorName}</span>
          <span aria-hidden>·</span>
          <span>{focusedTrace.hostId}</span>
          <span aria-hidden>·</span>
          <span>{formatTime(focusedTrace.startedAt)}</span>
          {focusedTrace.endedAt && (
            <>
              <span aria-hidden>·</span>
              <span>{formatDuration(focusedTrace.endedAt - focusedTrace.startedAt)} end to end</span>
            </>
          )}
        </div>
      </header>

      {focusedTrace.narration && (
        <div className={`th-verdict th-verdict--${focusedTrace.outcome}`} role="status">
          <h3>{focusedTrace.narration.headline}</h3>
          <p>{focusedTrace.narration.plain}</p>
          <p className="th-proves">
            <strong>What this proves: </strong>
            {focusedTrace.narration.proves}
            {focusedTrace.narration.seeItIn && <> Verify it independently in {focusedTrace.narration.seeItIn}.</>}
          </p>
        </div>
      )}

      {pending && (
        <div className="th-approve">
          <p>
            Held for {pending.approvers.join(' or ')}. Nothing has executed. Release it here, or approve it in the
            AgentGuard console — both resolve the same action.
          </p>
          <ApprovalControls
            approvers={approvers}
            onResolve={(decision, approverId) => resolveApproval(pending.traceId, decision, approverId)}
          />
        </div>
      )}

      <div className="th-body">
        {TRACE_STAGES.map(stage => {
          const step = focusedTrace.steps.find(candidate => candidate.id === stage.id);
          if (!step) return null;
          return <Stage key={stage.id} stage={stage} step={step} />;
        })}
      </div>

      {focusedTrace.mirror && (
        <div className="th-mirror">
          <h4>Read back from AgentGuard</h4>
          {focusedTrace.mirror.found ? (
            <>
              Asked independently, the product reports this action as{' '}
              <strong>{focusedTrace.mirror.decision ?? 'recorded'}</strong>
              {focusedTrace.mirror.policyName && <> under “{focusedTrace.mirror.policyName}”</>}. Visible in{' '}
              {focusedTrace.mirror.surfaces.join(', ')}.
            </>
          ) : (
            focusedTrace.mirror.error
          )}
        </div>
      )}
    </div>
  );
}

function Stage({ stage, step }: { stage: (typeof TRACE_STAGES)[number]; step: TraceStep }) {
  // The stage that decided the outcome opens itself; the rest stay closed until
  // asked for. A presenter should be able to say "and here is why" and click once.
  const decisive = useMemo(() => isDecisive(step), [step]);
  const [open, setOpen] = useState(decisive);

  useEffect(() => {
    if (decisive) setOpen(true);
  }, [decisive]);

  const hasDetail = Boolean(step.detail);
  const duration = step.startedAt && step.endedAt ? step.endedAt - step.startedAt : undefined;

  return (
    <div className="th-stage" data-status={step.status}>
      {hasDetail ? (
        <button
          type="button"
          className="th-stage__head"
          aria-expanded={open}
          onClick={() => setOpen(value => !value)}
        >
          <span className="th-stage__title">{stage.title}</span>
          <span className="mb-sr">{STEP_STATUS_LABEL[step.status]}</span>
          {duration !== undefined && <span className="th-stage__ms">{formatDuration(duration)}</span>}
        </button>
      ) : (
        <div className="th-stage__head">
          <span className="th-stage__title">{stage.title}</span>
          <span className="mb-sr">{STEP_STATUS_LABEL[step.status]}</span>
        </div>
      )}

      <p className="th-stage__explains">{stage.explains}</p>
      {step.summary && <p className="th-stage__summary">{step.summary}</p>}
      {open && step.detail && <StageDetail detail={step.detail} />}
    </div>
  );
}

/** The stage whose outcome the audience is meant to be looking at. */
function isDecisive(step: TraceStep): boolean {
  if (step.status === 'failed') return true;
  if (step.id === 'decision') return true;
  if (step.id === 'approval' && step.status === 'waiting') return true;
  if (step.id === 'execution' && (step.status === 'running' || step.status === 'waiting')) return true;
  if (step.detail?.kind === 'canonicalized' && step.detail.changed) return true;
  if (step.detail?.kind === 'response_scan' && step.detail.flagged) return true;
  return false;
}

function ApprovalControls({
  approvers,
  onResolve,
}: {
  approvers: any[];
  onResolve: (decision: 'approved' | 'rejected', approverId: string) => Promise<void>;
}) {
  const [approverId, setApproverId] = useState(approvers[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  const resolve = async (decision: 'approved' | 'rejected') => {
    setBusy(true);
    try {
      await onResolve(decision, approverId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="th-approve__controls">
      <select
        className="mb-input"
        value={approverId}
        onChange={event => setApproverId(event.target.value)}
        aria-label="Approver"
      >
        {approvers.map(person => (
          <option key={person.id} value={person.id}>
            {person.fullName} — {person.role}
          </option>
        ))}
      </select>
      <Button variant="primary" size="sm" disabled={busy || !approverId} onClick={() => resolve('approved')}>
        Approve
      </Button>
      <Button size="sm" disabled={busy || !approverId} onClick={() => resolve('rejected')}>
        Reject
      </Button>
    </div>
  );
}
